import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const WATemplateAdmin = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/wa/admin/templates?status=${filterStatus}&category=${filterCategory}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCategory]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const statusColors = {
    APPROVED: { bg: '#dcfce7', text: '#166534' },
    PENDING: { bg: '#fef3c7', text: '#92400e' },
    REJECTED: { bg: '#fee2e2', text: '#991b1b' },
    PAUSED: { bg: '#f3e8ff', text: '#6b21a8' },
    DISABLED: { bg: '#f3f4f6', text: '#374151' },
    IN_APPEAL: { bg: '#dbeafe', text: '#1e40af' },
    DELETED: { bg: '#f3f4f6', text: '#6b7280' },
  };

  const categoryColors = {
    MARKETING: { bg: '#dcfce7', text: '#166534' },
    UTILITY: { bg: '#dbeafe', text: '#1e40af' },
    AUTHENTICATION: { bg: '#f3e8ff', text: '#6b21a8' },
  };

  const qualityColors = {
    GREEN: '#22c55e', YELLOW: '#f59e0b', RED: '#ef4444', UNKNOWN: '#9ca3af',
  };

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  const filteredTemplates = templates.filter(t =>
    !searchTerm ||
    t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(t.waba_account_id).includes(searchTerm)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
            <Icons.FileText className="w-5 h-5 text-white" />
          </div>
          Template Management (Admin)
        </h1>
        <p className="text-sm text-gray-500 mt-1">Monitor and manage all WhatsApp message templates across all WABA accounts</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: 'Total Templates', value: templates.length || '0', icon: 'FileText', color: '#25D366' },
          { label: 'Approved', value: templates.filter(t => t.status === 'APPROVED').length || '0', icon: 'CheckCircle', color: '#128C7E' },
          { label: 'Pending', value: templates.filter(t => t.status === 'PENDING').length || '0', icon: 'Clock', color: '#F59E0B' },
          { label: 'Rejected', value: templates.filter(t => t.status === 'REJECTED').length || '0', icon: 'XCircle', color: '#ef4444' },
          { label: 'Paused', value: templates.filter(t => t.status === 'PAUSED').length || '0', icon: 'PauseCircle', color: '#6b21a8' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}14`, color: stat.color }}>
                {getIcon(stat.icon, 18)}
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-[11px] text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search template name or WABA ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-400"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Status:</span>
            <div className="flex rounded-xl border border-gray-200 overflow-hidden">
              {['all', 'APPROVED', 'PENDING', 'REJECTED', 'PAUSED'].map((s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className="px-2.5 py-1.5 text-[11px] font-medium transition-all"
                  style={{
                    background: filterStatus === s ? '#25D366' : 'white',
                    color: filterStatus === s ? 'white' : '#6b7280',
                  }}
                >
                  {s === 'all' ? 'All' : s}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Category:</span>
            <div className="flex rounded-xl border border-gray-200 overflow-hidden">
              {['all', 'MARKETING', 'UTILITY', 'AUTHENTICATION'].map((c) => (
                <button
                  key={c}
                  onClick={() => setFilterCategory(c)}
                  className="px-2.5 py-1.5 text-[11px] font-medium transition-all"
                  style={{
                    background: filterCategory === c ? '#25D366' : 'white',
                    color: filterCategory === c ? 'white' : '#6b7280',
                  }}
                >
                  {c === 'all' ? 'All' : c}
                </button>
              ))}
            </div>
          </div>
          <button onClick={fetchTemplates} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-500">
            <Icons.RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Template List */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
            <p className="text-sm text-gray-500">Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
              <Icons.FileText className="w-8 h-8" style={{ color: '#25D366' }} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No Templates Found</h3>
            <p className="text-sm text-gray-500">No WhatsApp message templates match your filters.</p>
          </div>
        ) : (
          <div className="space-y-0">
            {filteredTemplates.map((tpl) => (
              <div key={tpl.id} className="p-4 border-b border-gray-50 hover:bg-gray-50/50 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(37, 211, 102, 0.08)' }}>
                      <Icons.FileText className="w-5 h-5" style={{ color: '#25D366' }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm">{tpl.name}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-medium" style={{
                          background: categoryColors[tpl.category]?.bg || '#f3f4f6',
                          color: categoryColors[tpl.category]?.text || '#374151',
                        }}>
                          {tpl.category}
                        </span>
                        <span className="text-[10px] text-gray-400">Lang: {tpl.language}</span>
                        <span className="text-[10px] text-gray-400">WABA: #{tpl.waba_account_id}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {tpl.quality_score && (
                      <div className="flex items-center gap-1">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: qualityColors[tpl.quality_score] || '#9ca3af' }} />
                        <span className="text-[10px] text-gray-500">{tpl.quality_score}</span>
                      </div>
                    )}
                    <span className="px-2.5 py-1 rounded-lg text-xs font-semibold" style={{
                      background: statusColors[tpl.status]?.bg || '#f3f4f6',
                      color: statusColors[tpl.status]?.text || '#374151',
                    }}>
                      {tpl.status}
                    </span>
                    <div className="flex items-center gap-1">
                      <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Preview">
                        <Icons.Eye className="w-4 h-4" />
                      </button>
                      <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Sync from Meta">
                        <Icons.RefreshCw className="w-4 h-4" />
                      </button>
                      {tpl.status === 'REJECTED' && (
                        <button className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="Appeal">
                          <Icons.MessageSquare className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {tpl.total_sent > 0 && (
                  <div className="flex items-center gap-6 text-xs text-gray-500 mt-2 pt-2 border-t border-gray-50 ml-14">
                    <span>Sent: <strong className="text-gray-700">{(tpl.total_sent || 0).toLocaleString()}</strong></span>
                    <span>Delivered: <strong className="text-green-600">{(tpl.total_delivered || 0).toLocaleString()}</strong></span>
                    <span>Read: <strong className="text-blue-600">{(tpl.total_read || 0).toLocaleString()}</strong></span>
                    <span>Failed: <strong className="text-red-600">{(tpl.total_failed || 0).toLocaleString()}</strong></span>
                    {tpl.last_used_at && <span className="ml-auto text-gray-400">Last used: {tpl.last_used_at}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WATemplateAdmin;
